import { getItemFromNestedEadTree } from 'utils/get-item-from-nested-ead-tree';
import { useEffect, useState } from 'react';

import { ArchivalAidMetadata, DomainObject } from 'types';

import { GridBreadcrumb, GridEntityConfig } from '../grid-types';
import _ from 'lodash';

import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { SvgIconTypeMap } from '@material-ui/core';

import RootIcon from '@material-ui/icons/Work';
import AidIcon from '@material-ui/icons/MenuBook';
import SeriesIcon from '@material-ui/icons/AccountTree';
import FolderIcon from '@material-ui/icons/FolderOpen';
import ItemIcon from '@material-ui/icons/Description';
import ItemPart from '@material-ui/icons/FilterNone';

interface GridBreadcrumbsHook {
  id?: string;
  item?: DomainObject;
  level: number;
  gridConfig: GridEntityConfig[];
  isEad: boolean;
  rootArchivalAid?: ArchivalAidMetadata;
  archivalAidLevel?: number;
}

/*
Pomůcka: '@material-ui/icons/MenuBook';
Série: '@material-ui/icons/AccountTree';
Složka: '@material-ui/icons/FolderOpen';
Jednotlivost: '@material-ui/icons/Description'; (stávající
Část jednotlivosti: '@material-ui/icons/FilterNone';
*/

export const levelIcon: Record<
  string,
  OverridableComponent<SvgIconTypeMap<{}, 'svg'>>
> = {
  fonds: RootIcon,
  aid: AidIcon,
  series: SeriesIcon,
  file: FolderIcon,
  item: ItemIcon,
  otherlevel: ItemPart,
};

export const levelIndexToIcon: Record<
  number,
  OverridableComponent<SvgIconTypeMap<{}, 'svg'>>
> = {
  0: RootIcon,
  1: AidIcon,
  2: SeriesIcon,
  3: FolderIcon,
  4: ItemIcon,
  5: ItemPart,
};

export const LevelDefaultIcon = RootIcon;

export const useGridBreadcrumbs = (props: GridBreadcrumbsHook) => {
  const {
    id,
    item,
    level,
    gridConfig,
    rootArchivalAid,
    isEad,
    archivalAidLevel = 0,
  } = props;
  const [breadcrumbs, setBreadcrumbs] = useState<GridBreadcrumb[]>([]);

  useEffect(() => {
    const result: GridBreadcrumb[] = [];

    if (id === item?.id) {
      let hierarchyItem = item;

      for (let i = level; i >= 0; i--) {
        if (hierarchyItem) {
          const config =
            gridConfig[i >= archivalAidLevel + 1 ? archivalAidLevel + 1 : i];

          const itemId = hierarchyItem.id;
          const itemLabel = config.getLabel(hierarchyItem);
          const itemTimeRange = _.get(hierarchyItem, 'timeRange');
          const itemLevel = _.get(hierarchyItem, 'level');
          const index = i;

          const itemTo = `${gridConfig[0].urlRoute}/${i}/${hierarchyItem.id}${
            isEad && i > archivalAidLevel ? '/ead' : ''
          }`;

          result.push({
            id: itemId,
            label: itemLabel,
            to: itemTo,
            level: itemLevel,
            timeRange: itemTimeRange,
            index,
          });

          hierarchyItem = getHierarchyItem(
            i,
            hierarchyItem,
            config.getParent,
            rootArchivalAid,
            isEad,
            archivalAidLevel
          );
        }
      }
      setBreadcrumbs(result.reverse());
    }
  }, [archivalAidLevel, gridConfig, id, isEad, item, level, rootArchivalAid]);

  return breadcrumbs;
};

const getHierarchyItem = (
  level: number,
  hierarchyItem: DomainObject,
  getParent: ((item: DomainObject) => DomainObject) | undefined,
  rootArchivalAid: ArchivalAidMetadata | undefined,
  isEad: boolean,
  archivalAidLevel: number = 0
) => {
  if (level === 0) {
    return undefined;
  }
  //can be either ArchivalUnit or EadNode
  if (level === archivalAidLevel + 1) {
    // if is ead, return rootArchivalAid as parent.
    if (isEad) {
      return rootArchivalAid;
    }
  }

  if (level > archivalAidLevel + 1) {
    //if is level is different simply return EadNode
    return getItemFromNestedEadTree(
      hierarchyItem?.parent?.id,
      rootArchivalAid?.eadNode
    );
  }

  //if is not ead, return result from getParent function
  return getParent?.(hierarchyItem);
};
